// @flow
import 'react-resizable/css/styles.css'

import type { UseSignatureMemo } from 'hooks/useSignatureMemo'
import * as React from 'react'
import { withRouter } from 'react-router-dom'
import type { Answer } from 'types/answer'
import type { PDFFillerContextType, ViewMode } from 'types/pdf-filler-context'

type Props = {
  children: React.Node,
  viewMode?: ViewMode,
  resourceId?: string,
  signatureMemo: ?UseSignatureMemo,
  isTablet?: boolean,
  isKiosk?: boolean,
}

type InjectedProps = {
  match: {
    params: {
      resourceId: string,
      viewMode: ViewMode,
    },
    path: string,
  },
}

type ExtraContext = { [string]: any }

export const PDFFillerContext: React.Context<PDFFillerContextType> =
  React.createContext<PDFFillerContextType>({
    answers: {},
    dirty: false,
    documentName: '',
    documentDownloadable: false,
    redirectUrl: '',
    completePage: undefined,
    pdfReportType: null,
    error: '',
    errors: {},
    extraContexts: {},
    inputFields: {},
    inputRefs: [],
    isDragging: false,
    isSubmitted: false,
    lastAppliedSettings: {},
    notificationUsers: [],
    overlayActive: null,
    pages: [],
    pagesContainerRef: { current: null },
    pageRefs: {},
    removedInputFields: [],
    resourceId: '',
    responseId: null,
    selectedInputFieldId: null,
    setAnswers: () => {},
    setDirty: () => {},
    setDocumentName: () => {},
    setDocumentDownloadable: () => {},
    setExtraContexts: () => {},
    setRedirectUrl: () => {},
    setCompletePage: () => {},
    setPdfReportType: () => {},
    setError: () => {},
    setErrors: () => {},
    setInputFields: () => {},
    setInputRefs: () => {},
    setIsDragging: () => {},
    setIsSubmitted: () => {},
    setLastAppliedSettings: () => {},
    setNotificationUsers: () => {},
    setOverlayActive: () => {},
    setPages: () => {},
    setPagesDimensions: () => {},
    setPageRefs: () => {},
    setRemovedInputFields: () => {},
    setSelectedInputFieldId: () => {},
    setSignatureAnswers: () => {},
    setSubmitting: () => {},
    settingsOpen: false,
    setSettingsOpen: () => {},
    setZoom: () => {},
    setResourceId: () => {},
    signatureAnswers: {},
    signatureMemo: null,
    submitting: false,
    viewMode: 'edit',
    zoom: 1,
    teamDetails: null,
    setTeamDetails: () => {},
    isTablet: false,
    isKiosk: false,
    internalForm: false,
    setInternalForm: () => {},
    autoSignOffEnabled: false,
    setAutoSignOffEnabled: () => {},
    elationPdfNonFax: false,
    setElationPdfNonFax: () => {},
  })

function PdfFillerContext({
  children,
  match,
  viewMode: viewModeProp,
  resourceId: resourceIdProp,
  signatureMemo,
  isTablet,
  isKiosk,
}: Props & InjectedProps): React.Node {
  const [answers, setAnswers] = React.useState<{ [string]: Answer }>({})
  const [documentName, setDocumentName] = React.useState('')
  const [documentDownloadable, setDocumentDownloadable] = React.useState(false)
  // @deprecated
  const [redirectUrl, setRedirectUrl] = React.useState('')

  const [completePage, setCompletePage] = React.useState<
    CompletePageType | undefined,
  >(undefined)
  const [pdfReportType, setPdfReportType] = React.useState<?string>(null)
  const [error, setError] = React.useState('')
  const [errors, setErrors] = React.useState({})
  const [inputFields, _setInputFields] = React.useState({})
  const [inputRefs, setInputRefs] = React.useState([])
  const [isDragging, setIsDragging] = React.useState(false)
  const [pages, setPages] = React.useState([])
  const [removedInputFields, setRemovedInputFields] = React.useState([])
  const [selectedInputFieldId, setSelectedInputFieldId] = React.useState(null)
  const [isSubmitted, setIsSubmitted] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)
  const [signatureAnswers, setSignatureAnswers] = React.useState({})
  const [dirty, setDirty] = React.useState(false)
  const [pageRefs, setPageRefs] = React.useState({})
  const [zoom, setZoom] = React.useState(0.5)
  const [teamDetails, setTeamDetails] = React.useState(null)
  const [overlayActive, setOverlayActive] = React.useState(null)
  const [resourceId, setResourceId] = React.useState(
    resourceIdProp || match.params.resourceId || ''
  )
  const [settingsOpen, setSettingsOpen] = React.useState(false)
  const [lastAppliedSettings, setLastAppliedSettings] = React.useState({})
  const [notificationUsers, setNotificationUsers] = React.useState([])
  const [extraContexts, setExtraContexts] = React.useState<ExtraContext>({})
  const [internalForm, setInternalForm] = React.useState(false)
  const [autoSignOffEnabled, setAutoSignOffEnabled] = React.useState(false)
  const [elationPdfNonFax, setElationPdfNonFax] = React.useState(false)

  const viewMode = viewModeProp || match.params.viewMode || 'fill'

  const setInputFields = React.useCallback((args: any) => {
    _setInputFields(args)
    setDirty(true)
  }, [])

  const pagesContainerRef = React.useRef(null)

  const values = {
    answers,
    dirty,
    documentDownloadable,
    documentName,
    error,
    errors,
    extraContexts,
    inputFields,
    inputRefs,
    isDragging,
    isKiosk,
    isSubmitted,
    isTablet,
    lastAppliedSettings,
    notificationUsers,
    overlayActive,
    pageRefs,
    pages,
    pagesContainerRef,
    pdfReportType,
    redirectUrl,
    completePage,
    removedInputFields,
    resourceId,
    selectedInputFieldId,
    setAnswers,
    setDirty,
    setDocumentDownloadable,
    setDocumentName,
    setError,
    setErrors,
    setExtraContexts,
    setInputFields,
    setInputRefs,
    setIsDragging,
    setIsSubmitted,
    setLastAppliedSettings,
    setNotificationUsers,
    setOverlayActive,
    setPageRefs,
    setPages,
    setPdfReportType,
    setRedirectUrl,
    setCompletePage,
    setRemovedInputFields,
    setResourceId,
    setSelectedInputFieldId,
    setSettingsOpen,
    setSignatureAnswers,
    setSubmitting,
    setTeamDetails,
    setZoom,
    settingsOpen,
    signatureAnswers,
    signatureMemo,
    submitting,
    teamDetails,
    viewMode,
    zoom,
    internalForm,
    setInternalForm,
    autoSignOffEnabled,
    setAutoSignOffEnabled,
    elationPdfNonFax,
    setElationPdfNonFax,
  }

  return (
    <PDFFillerContext.Provider value={values}>
      {children}
    </PDFFillerContext.Provider>
  )
}

const EnhancedPdfFillerContextProvider: React.ComponentType<Props> =
  withRouter(PdfFillerContext)

export default EnhancedPdfFillerContextProvider
